<template>
  <div class="condition flex flex-col w-full flex-1">
    <div class="self-center text-xl text-right">if</div>
    <div class="event-dropdown-container relative">
      <div
        v-if="isEditable && siblingConditions.length"
        @click="$emit('deleteBonusRuleCondition', condition)"
        class="delete-condition-btn absolute hidden box-content w-5 h-5 z-10 -top-3 -left-2 text-blue bg-white text-center text-xl border border-solid border-blue rounded-full cursor-pointer hover:bg-blue-100">
        <div class="leading-5">&times;</div>
      </div>

      <!-- Event type -->
      <sb-async-dropdown
        :rows.sync="eventDropdown.rows"
        :selected.sync="eventDropdown.selected"
        :focused.sync="eventDropdown.focused"
        :options="eventDropdown.options"
        :disabled="!isEditable"
        @update:selected="updatedSelectedEvent"
        class="dropdown-component m-0 w-full"
        :class="{'has-error': !eventDropdown.selected}"
      >
        <template #label-only="{label}">
          <div class="label-text">
            {{ label }}
          </div>
        </template>
      </sb-async-dropdown>
    </div>

    <div v-if="!!eventDropdown.selected" class="self-center text-xl text-right">is</div>

    <!-- Event value -->
    <sb-async-dropdown
      v-if="inputType === 'select'"
      @update:selected="updateSelectedEventValue"
      :rows.sync="eventValuesDropdown.rows"
      :selected.sync="eventValuesDropdown.selected"
      :focused.sync="eventValuesDropdown.focused"
      :options="eventValuesDropdown.options"
      :disabled="!isEditable"
      class="dropdown-component event-option m-0"
      :class="{'has-error': !isEventOptionValueValid}"
      :headerRows="headerRows"
      :footerRows="footerRows"
    >
      <template #label-only="{label}">
        <div class="label-text">
          {{ label }}
        </div>
      </template>
    </sb-async-dropdown>

    <!-- Integer range -->
    <div v-if="inputType === 'integer_range'" class="integer-range  flex flex-auto">
      <input
        type="number"
        name="event_range_min"
        v-model="condition.event_range_min"
        placeholder="min"
        min="0"
        :max="condition.event_range_max ? Math.round(condition.event_range_max)-1 : 9999999"
        @keydown="preventNegativeNumber"
        :disabled="!isEditable"
        class="flex-auto m-0"
        :class="{'has-error': !isEventOptionIntegerRangeValid}"
      />
      <span class="px-2 self-center text-xl">to</span>
      <input
        type="number"
        name="event_range_max"
        v-model="condition.event_range_max"
        placeholder="max"
        :min="Math.round(condition.event_range_min)+1"
        max="10000000"
        @keydown="preventNegativeNumber"
        :disabled="!isEditable"
        class="flex-auto m-0"
        :class="{'has-error': !isEventOptionIntegerRangeValid}"
      />
    </div>

    <!-- Time range -->
    <div v-if="inputType === 'time_range'" class="date-range flex flex-auto">
      <sb-date
        :flatpickr="{ onClose: selectedDates => this.eventRangeMin = selectedDates[0] }"
        :value.sync="condition.event_range_min"
        :include="['time']"
        :disabled="!isEditable"
        format="hh:mm aa"
        placeholder="start time"
        class="flex-auto m-0 sb-white-input"
        :class="{'has-error': !isEventOptionTimeRangeValid}"
      />
      <span class="px-2 self-center text-xl">to</span>
      <sb-date
        :flatpickr="{ onClose: selectedDates => this.eventRangeMax = selectedDates[0] }"
        :value.sync="condition.event_range_max"
        :include="['time']"
        :disabled="!isEditable"
        format="hh:mm aa"
        placeholder="end time"
        class="flex-auto m-0 sb-white-input"
        :class="{'has-error': !isEventOptionTimeRangeValid}"
      />
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";

export default {
  name: "BonusEngineRuleCondition",
  props: {
    condition: Object,
    eventTypes: Array,
    eventValues: Object,
    eventValuesFilter: Object,
    filteredEventValues: Object,
    isEditable: Boolean,
    siblingConditions: Array
  },
  data() {
    return {
      eventDropdown: {
        rows: ['loading-row'],
        selected: null,
        focused: false,
        options: {
          label: selected => selected ? selected.label?.toLowerCase() : 'select an event'
        }
      },
      eventsOptionsLabels: {
        "day_of_the_week": "day of week",
        "time_of_day_range": "time of day",
        "category_purchased": "category",
        "brand_purchased": "brand",
        "product_purchased": "product",
        "transaction_total_range": "total",
        "points_earned_range": "points earned",
        "transaction_location": "location",
        "order_source": "order source",
        "reward_redeemed": "reward",
        "offer_redeemed": "offer"
      },
      eventValuesDropdown: {
        rows: ['loading-row'],
        selected: null,
        focused: false,
        options: {
          label: selected => selected?.label || `select ${this.eventsOptionsLabels[this.eventDropdown.selected?.key]}`
        }
      },
      footerRows: [],
      headerRows: []
    };
  },
  computed: {
    computedEventValues() {
      let eventValues = this.eventValues || {};
      if (this.eventValuesDropdown.focused && this.filteredEventValues) {
        eventValues = this.filteredEventValues;
      }
      return eventValues;
    },
    filteredEventTypes() {
      return this.eventTypes.filter(event =>
        this.condition.condition_type === 'optional'
        || !this.siblingConditionsEventsTypesKeys.includes(event.key)
      );
    },
    inputType() {
      return this.eventDropdown.selected?.input_type;
    },
    isConditionValid() {
      return this.isEventOptionValid;
    },
    isEventOptionIntegerRangeValid() {
      return !(
        [null, ''].includes(this.condition.event_range_min)
        || [null, ''].includes(this.condition.event_range_max)
        || !Number.isInteger(parseInt(this.condition.event_range_min))
        || !Number.isInteger(parseInt(this.condition.event_range_max))
        || parseInt(this.condition.event_range_min) >= parseInt(this.condition.event_range_max)
      );
    },
    isEventOptionTimeRangeValid() {
      return !(
        [null, ''].includes(this.condition.event_range_min)
        || [null, ''].includes(this.condition.event_range_max)
        || this.condition.event_range_min >= this.condition.event_range_max
      );
    },
    isEventOptionValid() {
      let isValid = false;
      if (this.inputType === "select") {
        isValid = this.isEventOptionValueValid;
      } else if (this.inputType === "integer_range") {
        isValid = this.isEventOptionIntegerRangeValid;
      } else if (this.inputType === "time_range") {
        isValid = this.isEventOptionTimeRangeValid;
      }
      return isValid;
    },
    isEventOptionValueValid() {
      return this.condition.event_value !== null;
    },
    siblingConditionsEventsTypesKeys() {
      return this.siblingConditions.map(condition => condition.event_type);
    }
  },
  watch: {
    condition: {
      deep: true,
      handler() {
        this.updateDropdowns();
      }
    },
    eventValues() {
      this.setEventValuesDropdownRows();
    },
    'eventValuesDropdown.focused'(hasFocus) {
      // Reset search on blur
      if (!hasFocus) {
        if (this.headerRows.length) this.headerRows = [{
          ...this.headerRows[0],
          state: {value: '', ...this.headerRows.state}
        }];
        this.$emit(
          "update:eventValuesFilter",
          null
        );
      }
    },
    filteredEventTypes() {
      this.generateEventTypesRows();
    },
    filteredEventValues() {
      this.setEventValuesDropdownRows();
    },
    isConditionValid(val) {
      this.condition.is_valid = val;
    }
  },
  created() {
    this.updateDropdowns();
  },
  methods: {
    generateEventTypesRows() {
      this.eventDropdown.rows = this.filteredEventTypes
                                    .map(eventType => ({slot: 'select-row', state: eventType}));
    },
    preventNegativeNumber(event) {
      if (event.key === '-') {
        event.preventDefault();
      }
    },
    setEventValuesBodyRows() {
      let eventValues = this.eventValues || {};
      if (this.eventValuesDropdown.focused && this.filteredEventValues) {
        eventValues = this.filteredEventValues;
      }
      this.eventValuesDropdown.rows = eventValues.data.map(row => {
        return {
          slot: 'select-row',
          state: row
        };
      });
    },
    setEventValuesDropdownRows() {
      if (this.inputType === 'select') {
        this.setEventValuesBodyRows();
        this.setEventValuesHeaderRows();
        this.setEventValuesFooterRows();
      }
    },
    setEventValuesFooterRows() {
      if (this.computedEventValues.pagination) {
        this.footerRows = [{
          slot: 'pagination-row',
          state: {
            page: this.computedEventValues.pagination.current_page - 1, //API is not zero-indexed
            pages: this.computedEventValues.pagination.total_pages,
            gap: 2,
            variant: 'dotted',
            update: page => {
              this.$emit(
                "update:eventValuesFilter",
                {...this.eventValuesFilter, eventType: this.condition.event_type, page}
              );
            }
          }
        }];
      } else {
        this.footerRows = [];
      }
    },
    setEventValuesHeaderRows() {
      if (this.computedEventValues.pagination) {
        this.headerRows = [{
          slot: 'query-row',
          state: {
            value: this.headerRows[0]?.state?.value || '',
            update: debounce(searchString => {
              this.$emit(
                "update:eventValuesFilter",
                {...this.eventValuesFilter, eventType: this.condition.event_type, page: 0, searchString}
              );
            }, 500)
          }
        }];
      } else {
        this.headerRows = [];
      }
    },
    updateDropdowns() {
      this.generateEventTypesRows();
      this.eventDropdown.selected = this.eventDropdown.rows.find(row => row.state.key === this.condition.event_type)?.state;
      this.setEventValuesDropdownRows();
      this.eventValuesDropdown.selected = {key: this.condition.event_value, label: this.condition.event_value_label};
    },
    updateSelectedEventValue(selectedEventValue) {
      this.condition.event_value_label = selectedEventValue.label;
      this.condition.event_value = selectedEventValue.key;
    },
    updatedSelectedEvent(selectedEvent) {
      this.eventValuesDropdown.selected = null;
      this.condition.event_type = selectedEvent.key;
      for (const prop of ['event_value', 'event_value_label', 'event_range_min', 'event_range_max']) {
        this.condition[prop] = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.sb-date::v-deep .label-container {
  white-space: nowrap;
  height: 40px;
  padding: .5rem;
  font-size: 1rem;
}

input.has-error::v-deep {
  border-color: red;
  color: red;
}

.dropdown-component.has-error::v-deep,
.sb-date.has-error::v-deep {
  .label-container {
    border-color: red;
    color: red;
  }
  .sb-content {
    border-color: red;
  }
}

.event-dropdown-container:hover .delete-condition-btn {
  display: block;
}
</style>
